import { searchGamesIndex } from "entities/game/constants";
import { Game } from "entities/game/types";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formatAlgoliaHits } from "shared/utils/formatAlgoliaHits";

type UseGamesSearchProps = {
  isActive?: string[];
  filterTags?: string[];
  initialResultsCount?: number;
  searchResultsCount?: number;
};

export const useGamesSearch = ({
  isActive = ["true"],
  filterTags = [],
  initialResultsCount = 20,
  searchResultsCount = 50,
}: UseGamesSearchProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [games, setGames] = useState<Game[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoaded, setIsInitialLoaded] = useState(false);

  const isActiveFilter =
    isActive.length > 0
      ? `(${isActive.map((id) => `isActive:${id}`).join(" OR ")})`
      : "";

  const filterTagsString = filterTags.filter(Boolean).some((tag) => !!tag)
    ? filterTags.filter(Boolean).join(" AND ")
    : "";

  const filters = [isActiveFilter, filterTagsString]
    .filter(Boolean)
    .join(" AND ");

  const searchGames = useCallback(
    async (query: string) => {
      setIsLoading(true);

      try {
        const { hits } = await searchGamesIndex.search<Game>(query, {
          filters,
          hitsPerPage: query.trim() ? searchResultsCount : initialResultsCount,
        });

        const formattedHits = formatAlgoliaHits<Game>(hits);
        setGames(formattedHits);

        if (!query.trim() && !isInitialLoaded) {
          setIsInitialLoaded(true);
        }
      } catch (error) {
        console.error("Error searching games:", error);
        setGames([]);
      } finally {
        setIsLoading(false);
      }
    },
    [filters, initialResultsCount, searchResultsCount, isInitialLoaded],
  );

  const debouncedSearchGames = useMemo(
    () => debounce(searchGames, 300),
    [searchGames],
  );

  // Load initial games on mount
  useEffect(() => {
    if (!isInitialLoaded) {
      searchGames(""); // Empty query to get initial results
    }
  }, [searchGames, isInitialLoaded]);

  useEffect(() => {
    if (isInitialLoaded) {
      debouncedSearchGames(searchQuery);
    }

    return () => {
      debouncedSearchGames.cancel();
    };
  }, [searchQuery, debouncedSearchGames, isInitialLoaded]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  return {
    games,
    isLoading,
    searchQuery,
    onSearchChange: handleSearchChange,
    isInitialLoaded,
  };
};
